import { OrcamentoIcon, OrcamentoDialog, OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/* ============================================================
   CONFIGURAÇÕES (desktop) — gestão de contactos úteis:
   grupos, criação/edição de contactos e importação de listagens
   (ex.: lojas GALP a partir de Excel).
   ============================================================ */

const vazio = { nome: '', cargo: '', telefone: '', email: '', grupo: 'Geral', referencia: '', morada: '', codigo_postal: '', localidade: '', concelho: '', ordem: 0 };

function ContactoModal({ contacto, grupos, grupoAtivo, onClose, onSaved }) {
    const novo = !contacto;
    const [form, setForm] = useState(() => novo
        ? { ...vazio, grupo: grupoAtivo && grupoAtivo !== 'todos' ? grupoAtivo : 'Geral' }
        : { ...vazio, ...contacto });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const guardar = async () => {
        if (!form.nome.trim()) { setErr('Indique o nome.'); return; }
        setSaving(true); setErr('');
        try {
            if (novo) await api.post('/contactos-uteis', form);
            else await api.put(`/contactos-uteis/${contacto.id}`, form);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <OrcamentoDialog data-admin-dialog="contactos" onClose={onClose} style={overlay}>
            <div className="admin-modal-card" style={{ ...modal, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
                <div className="admin-modal-header" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? 'Novo contacto' : `Editar — ${contacto.nome}`}</h3>
                    <button aria-label="Fechar" onClick={onClose} style={closeBtn}><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>
                <div className="admin-modal-body">
                <div className="admin-form-grid" style={grid2}>
                    <div>
                        <label className="admin-field-label" style={lbl}>Nome *</label>
                        <input value={form.nome} onChange={e => upd('nome', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label className="admin-field-label" style={lbl}>Grupo</label>
                        <input value={form.grupo} onChange={e => upd('grupo', e.target.value)} list="grupos-list" style={{ ...inp, width: '100%' }} />
                        <datalist id="grupos-list">
                            {grupos.map(g => <option key={g.grupo} value={g.grupo} />)}
                        </datalist>
                    </div>
                </div>
                <div className="admin-form-grid" style={grid3}>
                    <div>
                        <label className="admin-field-label" style={lbl}>Cargo / função</label>
                        <input value={form.cargo} onChange={e => upd('cargo', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label className="admin-field-label" style={lbl}>Telefone</label>
                        <input value={form.telefone} onChange={e => upd('telefone', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label className="admin-field-label" style={lbl}>Email</label>
                        <input value={form.email} onChange={e => upd('email', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div className="admin-form-grid" style={grid2}>
                    <div>
                        <label className="admin-field-label" style={lbl}>Referência / ID (ex.: nº posto)</label>
                        <input value={form.referencia} onChange={e => upd('referencia', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label className="admin-field-label" style={lbl}>Concelho</label>
                        <input value={form.concelho} onChange={e => upd('concelho', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label className="admin-field-label" style={lbl}>Morada</label>
                    <input value={form.morada} onChange={e => upd('morada', e.target.value)} style={{ ...inp, width: '100%' }} />
                </div>
                <div className="admin-form-grid" style={grid2}>
                    <div>
                        <label className="admin-field-label" style={lbl}>Código postal</label>
                        <input value={form.codigo_postal} onChange={e => upd('codigo_postal', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label className="admin-field-label" style={lbl}>Localidade</label>
                        <input value={form.localidade} onChange={e => upd('localidade', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                {err && <div style={{ color: '#e53e3e', margin: '8px 0', fontSize: '0.85rem' }}>{err}</div>}
                </div>
                <div className="admin-modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                    <button onClick={onClose} style={btnSec}>Cancelar</button>
                    <button onClick={guardar} disabled={saving} className="admin-primary" style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </OrcamentoDialog>
    );
}

function ImportarModal({ grupos, onClose, onSaved }) {
    const { notify } = useOrcamentoFeedback();
    const [grupo, setGrupo] = useState('');
    const [substituir, setSubstituir] = useState(false);
    const [ficheiro, setFicheiro] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const importar = async () => {
        if (!grupo.trim()) { setErr('Indique o grupo de destino.'); return; }
        if (!ficheiro) { setErr('Escolha o ficheiro Excel.'); return; }
        setSaving(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('grupo', grupo.trim());
            fd.append('substituir', substituir ? '1' : '0');
            fd.append('ficheiro', ficheiro);
            const r = await api.post('/contactos-uteis/importar-excel', fd, { timeout: 120000 });
            await notify(`Importados ${r.data.inseridos} contactos para o grupo "${r.data.grupo}".`);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro na importação'); }
        finally { setSaving(false); }
    };

    return (
        <OrcamentoDialog data-admin-dialog="contactos" onClose={onClose} style={overlay}>
            <div className="admin-modal-card" style={{ ...modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                <div className="admin-modal-header" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Importar listagem (Excel)</h3>
                    <button aria-label="Fechar" onClick={onClose} style={closeBtn}><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>
                <div className="admin-modal-body">
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 10 }}>
                    Formato esperado (como a listagem GALP): colunas <strong>Posto | Concelho | ID | Localidade | Morada | Código Postal</strong>, com o cabeçalho na linha 1 ou 2.
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label className="admin-field-label" style={lbl}>Grupo de destino</label>
                    <input value={grupo} onChange={e => setGrupo(e.target.value)} list="grupos-imp" placeholder="ex.: GALP" style={{ ...inp, width: '100%' }} />
                    <datalist id="grupos-imp">
                        {grupos.map(g => <option key={g.grupo} value={g.grupo} />)}
                    </datalist>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label className="admin-field-label" style={lbl}>Ficheiro Excel</label>
                    <input type="file" accept=".xlsx,.xls" onChange={e => setFicheiro(e.target.files?.[0] || null)} style={{ fontSize: '0.85rem' }} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#475569', cursor: 'pointer', marginBottom: 6 }}>
                    <input type="checkbox" checked={substituir} onChange={e => setSubstituir(e.target.checked)} />
                    Substituir o grupo (apaga os contactos existentes do grupo antes de importar)
                </label>
                {err && <div style={{ color: '#e53e3e', margin: '8px 0', fontSize: '0.85rem' }}>{err}</div>}
                </div>
                <div className="admin-modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                    <button onClick={onClose} style={btnSec}>Cancelar</button>
                    <button onClick={importar} disabled={saving} className="admin-primary" style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A importar…' : 'Importar'}</button>
                </div>
            </div>
        </OrcamentoDialog>
    );
}

const ConfiguracoesContent = () => {
    const { confirm, notify } = useOrcamentoFeedback();
    const [grupos, setGrupos] = useState([]);
    const [contactos, setContactos] = useState([]);
    const [grupoAtivo, setGrupoAtivo] = useState('todos');
    const [pesquisa, setPesquisa] = useState('');
    const [loading, setLoading] = useState(true);
    const [editar, setEditar] = useState(null); // null | {} (novo) | {contacto}
    const [importar, setImportar] = useState(false);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [c, g] = await Promise.all([
                api.get('/contactos-uteis', { params: { _t: Date.now() } }),
                api.get('/contactos-uteis/grupos', { params: { _t: Date.now() } }),
            ]);
            setContactos(c.data || []);
            setGrupos(g.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const eliminar = async (c) => {
        if (!await confirm(`Eliminar o contacto "${c.nome}"?`)) return;
        try { await api.delete(`/contactos-uteis/${c.id}`); carregar(); }
        catch { await notify('Erro ao eliminar'); }
    };

    const eliminarGrupo = async (grupo) => {
        const total = grupos.find(g => g.grupo === grupo)?.total || 0;
        if (!await confirm(`Eliminar o grupo "${grupo}" e os seus ${total} contactos? Esta ação é definitiva.`)) return;
        try {
            await api.delete(`/contactos-uteis/grupos/${encodeURIComponent(grupo)}`);
            setGrupoAtivo('todos');
            carregar();
        } catch { await notify('Erro ao eliminar grupo'); }
    };

    let visiveis = grupoAtivo === 'todos' ? contactos : contactos.filter(c => (c.grupo || 'Geral') === grupoAtivo);
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        visiveis = visiveis.filter(c =>
            (c.nome || '').toLowerCase().includes(t) || (c.referencia || '').toLowerCase().includes(t) ||
            (c.morada || '').toLowerCase().includes(t) || (c.localidade || '').toLowerCase().includes(t) ||
            (c.concelho || '').toLowerCase().includes(t) || (c.telefone || '').toLowerCase().includes(t));
    }
    const limitado = visiveis.length > 300;
    const mostrar = limitado ? visiveis.slice(0, 300) : visiveis;

    return (
        <div className="page-content admin-theme-page admin-config-page" style={{ padding: 24 }}>
            <div className="admin-page-header" style={{ marginBottom: 16 }}>
                <div><h1 style={{ margin: 0, fontSize: '1.4rem' }}><OrcamentoIcon name="settings" fallback="🛠️" /> Configurações</h1>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>Contactos úteis — grupos, edição e importação de listagens</p></div>
            </div>

            <div className="admin-toolbar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
                <button aria-pressed={grupoAtivo === 'todos'} onClick={() => setGrupoAtivo('todos')}
                    style={{ ...chip, ...(grupoAtivo === 'todos' ? chipAtivo : {}) }}>
                    Todos ({contactos.length})
                </button>
                {grupos.map(g => (
                    <button aria-pressed={grupoAtivo === g.grupo} key={g.grupo} onClick={() => setGrupoAtivo(g.grupo)}
                        style={{ ...chip, ...(grupoAtivo === g.grupo ? chipAtivo : {}) }}>
                        {g.grupo} ({g.total})
                    </button>
                ))}
                <div style={{ flex: 1 }} />
                <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Pesquisar nome, ID, morada…" style={{ ...inp, width: 220 }} />
                <button onClick={() => setImportar(true)} style={btnSec}><OrcamentoIcon name="upload" fallback="📥" /> Importar Excel</button>
                <button onClick={() => setEditar({})} className="admin-primary" style={btnPri}><OrcamentoIcon name="plus" fallback="+" /> Novo contacto</button>
                {grupoAtivo !== 'todos' && (
                    <button onClick={() => eliminarGrupo(grupoAtivo)} style={{ ...btnSec, color: '#dc2626', borderColor: '#dc2626' }}><OrcamentoIcon name="trash" fallback="🗑" /> Eliminar grupo</button>
                )}
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p> : (
                <div className="table-responsive" style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                    <table className="standard-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Grupo</th>
                                <th>Ref./ID</th>
                                <th>Telefone</th>
                                <th>Email</th>
                                <th>Morada</th>
                                <th>Localidade</th>
                                <th>Concelho</th>
                                <th style={{ textAlign: 'right' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {mostrar.length === 0 && (
                                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: '28px 0' }}>Sem contactos.</td></tr>
                            )}
                            {mostrar.map(c => (
                                <tr key={c.id}>
                                    <td style={{ fontWeight: 600, maxWidth: 240 }}>{c.nome}</td>
                                    <td><span className="admin-badge" style={{ ...badge, background: '#e0f2fe', color: '#075985' }}>{c.grupo || 'Geral'}</span></td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{c.referencia || '—'}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{c.telefone || '—'}</td>
                                    <td style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email || '—'}</td>
                                    <td style={{ maxWidth: 240, fontSize: '0.82rem' }}>{c.morada || '—'}</td>
                                    <td style={{ fontSize: '0.82rem' }}>{c.localidade || '—'}</td>
                                    <td style={{ fontSize: '0.82rem' }}>{c.concelho || '—'}</td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button className="admin-icon-button" title="Editar contacto" onClick={() => setEditar({ contacto: c })} style={{ ...btnSec, ...mini }}><OrcamentoIcon name="edit" fallback="Editar" /></button>
                                        <button className="admin-icon-button admin-danger" title="Eliminar contacto" onClick={() => eliminar(c)} style={{ ...btnSec, ...mini, color: '#e53e3e' }}><OrcamentoIcon name="trash" fallback="✕" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {limitado && (
                        <div style={{ padding: '8px 12px', color: '#94a3b8', fontSize: '0.8rem' }}>
                            A mostrar os primeiros 300 de {visiveis.length} — use a pesquisa para refinar.
                        </div>
                    )}
                </div>
            )}

            {editar && <ContactoModal contacto={editar.contacto} grupos={grupos} grupoAtivo={grupoAtivo}
                onClose={() => setEditar(null)} onSaved={carregar} />}
            {importar && <ImportarModal grupos={grupos} onClose={() => setImportar(false)} onSaved={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.72rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '2px 9px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' };
const chip = { padding: '6px 14px', borderRadius: 16, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600 };
const chipAtivo = { background: '#3182ce', borderColor: '#3182ce', color: '#fff' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '4px 9px', marginLeft: 6, fontSize: '0.76rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 };

export default function Configuracoes() { return <OrcamentoFeedbackProvider><ConfiguracoesContent /></OrcamentoFeedbackProvider>; }
